/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Product Offerings Step Select Date component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.10.0
 */

import { useCallback, useEffect, useMemo } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { TimePickerWithClocks } from '@/main/components/common/TimePickerWithClocks/TimePickerWithClocks';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import { InfoBlock } from '../../../components/common';
import DatePicker from '../../../components/common/DatePicker/DatePicker';
import { IDatePickerAdditionalButton } from '../../../components/common/DatePicker/types/datePickerTypes';
import { EColorStatus } from '../../../modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardProductOfferings,
} from '../../../modules/internationalization/intSelectors';
import { IWizardProductOfferingsTranslation } from '../../../modules/internationalization/types';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { IWizardSummaryParam, WizardSummary } from '../../../modules/wizard/components/WizardSummary/WizardSummary';
import { IRootReducer } from '../../../reducers/rootReducer';
import { makeSelectFeatureEnabled } from '../../PageAuth/selectors/authSelectors';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import {
    productOfferingsSaveStorageAction,
    productOfferingsSetParamsAction,
    productOfferingsStartValidateAction,
} from '../wizardProductOfferingsActions';
import { EWizardProductOfferingsSteps } from '../wizardProductOfferingsConstants';
import {
    makeSelectProductOfferingsDateFrom,
    makeSelectProductOfferingsDateTo,
    makeSelectProductOfferingsIsDisconnect,
    makeSelectProductOfferingsIsTariff,
    makeSelectProductOfferingsProductOfferings,
    makeSelectProductOfferingsSubsCount,
    makeSelectProductOfferingsTimeFrom,
    makeSelectProductOfferingsTimeTo,
} from '../wizardProductOfferingsSelectors';
import { IProductOfferingsListItem, IProductOfferingsSetParamsActionPayload } from '../wizardProductOfferingsTypes';
import {
    StepSelectDateFilterStyled,
    StepSelectDateFilterWrapperStyled,
    StepSelectDateInfoStyled,
    StepSelectDateStyled,
} from './styled/StepSelectDateStyled';

const activationMinDate = moment();
const activationMaxDate = moment().add(5, 'year').subtract(1, 'day');
const deactivationMinDate = moment();
const deactivationMaxDate = moment().add(5, 'year');

type TStepSelectDateProps = IConnectedProps & IDispatchProps;

const StepSelectDate = (props: TStepSelectDateProps) => {
    const {
        productOfferingsList,
        saveStorage,
        setParams,
        startValidate,
        translationCommon,
        translationProductOfferings,

        dateTo,
        dateFrom,
        timeFrom,
        timeTo,
        isDisconnect,
        isTariff,
        subsCount,
    } = props;

    const stepTranslations = translationProductOfferings[EWizardProductOfferingsSteps.StepSelectDate];
    const isRoamingProductsFeatureEnabled =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS));

    const isFromDateAfterToday = dayjs(dateFrom).isValid() && dayjs(dateFrom).isAfter(dayjs(), 'day');
    const isToDateAfterToday = dayjs(dateTo).isValid() && dayjs(dateTo).isAfter(dayjs(), 'day');

    const isTimePickerTimeFromShown = isFromDateAfterToday && isRoamingProductsFeatureEnabled && !isTariff;

    const summaryParams: IWizardSummaryParam[] = [
        {
            title: !isDisconnect
                ? translationProductOfferings.wizardSummary.activationServices
                : translationProductOfferings.wizardSummary.deactivationServices,
            values: productOfferingsList.map((item) => item.name),
            limit: 4,
            limitMore: translationProductOfferings.wizardSummary.showMore,
            limitLess: translationProductOfferings.wizardSummary.showLess,
        },
        {
            title: translationProductOfferings.wizardSummary.subscribersAmount,
            values: [`${subsCount}`],
        },
    ];

    const deactivateButtons: IDatePickerAdditionalButton[] = [
        {
            onClick: () => setDate('dateTo'),
            title: translationCommon.calendar.additionalButtons.deselectDate,
        },
    ];

    useEffect(() => {
        const from = moment(dateFrom);
        const to = moment(dateTo);
        if (dateFrom && dateTo && from.unix() >= to.unix()) {
            setParams({
                dateTo: from.add(1, 'month').startOf('month').format(EFormatData.FullDataWithTimeZone),
            });
        }
        if (!isFromDateAfterToday) {
            setParams({
                timeFrom: '',
            });
        }
        if (!isToDateAfterToday) {
            setParams({
                timeTo: '',
            });
        }
    }, [dateFrom, dateTo, isFromDateAfterToday, isToDateAfterToday, setParams]);

    const textInfo = useMemo(() => {
        if (isRoamingProductsFeatureEnabled && !isTariff) {
            return isDisconnect
            ? stepTranslations.infoHtmlDisconnectWithRoamingProducts
            : stepTranslations.infoHtmlConnectWithRoamingProducts;
        } else {
            return isDisconnect
            ? stepTranslations.infoHtmlDisconnect
            : stepTranslations.infoHtmlConnect;
        }
    },[
        isDisconnect,
        isRoamingProductsFeatureEnabled,
        isTariff,
        stepTranslations.infoHtmlConnect,
        stepTranslations.infoHtmlConnectWithRoamingProducts,
        stepTranslations.infoHtmlDisconnect,
        stepTranslations.infoHtmlDisconnectWithRoamingProducts,
    ]);

    /*
     * -------------------------------------------
     *               Handlers
     * -------------------------------------------
     */

    const setDate = useCallback(
        (type: 'dateFrom' | 'dateTo', date?: Moment) => {
            setParams({
                [type]: date?.format(EFormatData.FullDataWithTimeZone),
            });
            saveStorage();
            startValidate();
        },
        [saveStorage, setParams, startValidate],
    );

    const setTime = useCallback(
        (type: 'timeFrom' | 'timeTo', time?: Dayjs) => {
            setParams({
                [type]: time?.format(EFormatData.FullTime),
            });
            saveStorage();
            startValidate();
        },
        [saveStorage, setParams, startValidate],
    )

    const setDateFromHandler = useCallback(
        (dateFrom: Moment) => {
            setDate('dateFrom', dateFrom);
        },
        [setDate],
    );

    const setDateToHandler = useCallback(
        (dateTo: Moment) => {
            setDate('dateTo', dateTo);
        },
        [setDate],
    );

    const setTimeFromHandler = useCallback(
        (time: Dayjs) => {
            setTime('timeFrom', time);
        },
        [setTime],
    );

    const setTimeToHandler = useCallback(
        (time: Dayjs) => {
            setTime('timeTo', time);
        },
        [setTime],
    );

    /*
     * -------------------------------------------
     *           Render component
     * -------------------------------------------
     */

    return (
        <StepSelectDateStyled>
            <div>
                <StepSelectDateFilterWrapperStyled>
                    {!isDisconnect && (
                        <StepSelectDateFilterStyled>
                            <DatePicker
                                isCurrentPeriod
                                dataName="wizard-product-offering-data-from"
                                label={stepTranslations.dateFromLabel}
                                maxDate={activationMaxDate}
                                minDate={activationMinDate}
                                selectedDate={dateFrom ? moment(dateFrom) : undefined}
                                translation={translationCommon}
                                onSelectDate={setDateFromHandler}
                            />
                            {isTimePickerTimeFromShown &&
                            <TimePickerWithClocks
                                defaultTimeValueHHmm={timeFrom || '00:00'}
                                translation={translationCommon}
                                onSelectTime={setTimeFromHandler}
                            />}

                        </StepSelectDateFilterStyled>
                    )}
                    {(!isTariff || isDisconnect) && (
                        <StepSelectDateFilterStyled>
                            <DatePicker
                                isCurrentPeriod
                                additionalButtons={deactivateButtons}
                                dataName="wizard-product-offering-data-to"
                                label={stepTranslations.dateToLabel}
                                maxDate={deactivationMaxDate}
                                minDate={dateFrom ? moment(dateFrom).add(1, 'day') : deactivationMinDate}
                                selectedDate={dateTo ? moment(dateTo) : undefined}
                                translation={translationCommon}
                                onSelectDate={setDateToHandler}
                            />
                            {!!dateTo && isRoamingProductsFeatureEnabled &&
                            (isDisconnect ?
                                isToDateAfterToday &&
                                <TimePickerWithClocks
                                    defaultTimeValueHHmm={timeTo || '00:00'}
                                    translation={translationCommon}
                                    onSelectTime={setTimeToHandler}
                                /> :
                                <TimePickerWithClocks
                                    defaultTimeValueHHmm={timeTo || '23:59'}
                                    translation={translationCommon}
                                    onSelectTime={setTimeToHandler}
                                />
                            )}
                        </StepSelectDateFilterStyled>
                    )}
                </StepSelectDateFilterWrapperStyled>

                <StepSelectDateInfoStyled>
                    <InfoBlock colorStatus={EColorStatus.WARNING}>
                        <Typography isDangerousInnerHTML>{textInfo}</Typography>
                    </InfoBlock>
                </StepSelectDateInfoStyled>
            </div>
            <div>
                <WizardSummary
                    params={summaryParams}
                    title={
                        !isDisconnect
                            ? translationProductOfferings.wizardSummary.activationTitle
                            : translationProductOfferings.wizardSummary.deactivationTitle
                    }
                />
            </div>
        </StepSelectDateStyled>
    );
};

interface IConnectedProps {
    dateFrom: string;
    dateTo: string;
    timeFrom: string;
    timeTo: string;
    isDisconnect: boolean;
    isTariff: boolean;
    productOfferingsList: IProductOfferingsListItem[];
    subsCount: number;

    translationCommon: ICommonTranslation;
    translationProductOfferings: IWizardProductOfferingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    dateFrom: makeSelectProductOfferingsDateFrom(),
    dateTo: makeSelectProductOfferingsDateTo(),
    timeFrom: makeSelectProductOfferingsTimeFrom(),
    timeTo: makeSelectProductOfferingsTimeTo(),
    isDisconnect: makeSelectProductOfferingsIsDisconnect(),
    isTariff: makeSelectProductOfferingsIsTariff(),
    productOfferingsList: makeSelectProductOfferingsProductOfferings(),
    subsCount: makeSelectProductOfferingsSubsCount(),

    translationCommon: makeSelectTranslationCommon(),
    translationProductOfferings: makeSelectTranslationWizardProductOfferings(),
});

interface IDispatchProps {
    saveStorage: () => void;
    setParams: (p: IProductOfferingsSetParamsActionPayload) => void;
    startValidate: () => void;
}

const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    saveStorage: () => dispatch(productOfferingsSaveStorageAction()),
    setParams: (payload) => dispatch(productOfferingsSetParamsAction(payload)),
    startValidate: () => dispatch(productOfferingsStartValidateAction()),
});

export default connect(mapStateToProps, mapDispatchProps)(StepSelectDate);
